import React, { useContext, useEffect, useState } from 'react';
import {
  BdsGrid,
  BdsPaper,
  BdsTypo,
  BdsTable,
  BdsTableHeader,
  BdsTableBody,
  BdsTableCell,
  BdsTableRow,
  BdsChipTag,
  BdsTableTh,
  BdsButtonIcon,
  BdsModal,
} from 'blip-ds/dist/blip-ds-react';
import { ColorChipTag } from 'blip-ds/dist/types/components/chip-tag/chip-tag';
import MtlsContext from '../../../../contexts/MtlsContext';
import RemoveCertificate from '../RemoveCertificate';
import { MtlsCertificate } from '../../../../models/MtlsCertificate';
import { BlipLoadingComponent } from '../../../../components/BlipLoading/BlipLoadingComponent';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { localization } from './localization';
import { formatDate } from '../../../../utils/date';
import { RequestStatus } from '../../../../shared/Requests';
import { showToast } from '../../../../api/CommonApi';
import ListHosts from '../ListHosts';

interface MtlsCertificateheader {
  name: string;
  url: string;
  expiration: string;
  status: string;
  actions: string;
}

interface statusValidation {
  status: string;
  color: string;
  text: string;
}

enum Status {
  VALID = 'valid',
  INVALID = 'invalid',
  UNDERVALIDATION = 'underValidation',
}

const ListCertificates: React.FC = () => {
  const {
    toggleHostsModal,
    openRemoveCertificateAlert,
    mtlsCertificateApi,
    tenantId,
    mtlsCertificate,
    setMtlsCertificate,
    certificateUpdatedProps,
    setCertificateUpdatedProps,
  } = useContext(MtlsContext);
  const [isLoading, setLoading] = useState(false);
  const content = useContentLocalizer(localization);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);

        const mtlsCertificateData = await mtlsCertificateApi.getMtlsCertificates(tenantId);
        if (mtlsCertificateApi.getStatus() == RequestStatus.FAILURE) {
          showToast({
            type: 'danger',
            message: content.errorToast,
          });
        }
        setMtlsCertificate(mtlsCertificateData);
      } finally {
        setCertificateUpdatedProps({ ...certificateUpdatedProps, certificateUpdated: false });
        setLoading(false);
      }
    };

    getData();
  }, [certificateUpdatedProps.certificateUpdated]);

  const heading: MtlsCertificateheader[] = [content.name, content.expiration, content.status, content.actions];

  const statusValidation = [
    { status: Status.VALID, color: 'success', text: content.validationStatus.valid },
    { status: Status.INVALID, color: 'disabled', text: content.validationStatus.invalid },
    { status: Status.UNDERVALIDATION, color: 'default', text: content.validationStatus.underValidation },
  ];

  const chipTagColor = (status: string): ColorChipTag => {
    const color = statusValidation.find(
      (item: statusValidation) => item.status.toLowerCase() === status.toLowerCase(),
    )?.color;
    return color ? (color as ColorChipTag) : 'default';
  };

  const chipTagText = (status: string): string => {
    const text = statusValidation.find(
      (item: statusValidation) => item.status.toLowerCase() === status.toLowerCase(),
    )?.text;
    return text ? text : content.validationStatus.underValidation;
  };

  const handleDeleteCertificateAlert = (id: string) => {
    setCertificateUpdatedProps({ ...certificateUpdatedProps, certificateId: id });
    openRemoveCertificateAlert();
  };

  const handleEditCertificateAlert = (id: string) => {
    setCertificateUpdatedProps({ ...certificateUpdatedProps, certificateId: id });
    toggleHostsModal();
  };

  return (
    <BdsPaper>
      <BdsGrid padding="3">
        <BdsGrid xxs="12" direction="column">
          <BdsGrid margin="b-2" direction="column">
            <BdsTypo variant="fs-24" bold="bold" margin={false}>
              {content.title}
            </BdsTypo>
            <BdsTypo variant="fs-16">{content.subtitle}</BdsTypo>
          </BdsGrid>
          {isLoading ? (
            <BlipLoadingComponent align="center" />
          ) : (
            <BdsTable id='certificates-table'>
              <BdsTableHeader>
                <BdsTableRow>
                  {heading.map((item, index) => {
                    return <BdsTableTh key={index}>{item}</BdsTableTh>;
                  })}
                </BdsTableRow>
              </BdsTableHeader>
              <BdsTableBody>
                {mtlsCertificate?.map((row: MtlsCertificate, index) => {
                  return (
                    <BdsTableRow key={index} data-testid={row.certificate_id}>
                      <BdsTableCell style={{ minWidth: '20rem' }} title={row.description}>
                        <BdsTypo variant="fs-14">{row.description}</BdsTypo>
                      </BdsTableCell>
                      <BdsTableCell>
                        <BdsTypo variant="fs-14">{formatDate(new Date(row.expiration_date), content.locale)}</BdsTypo>
                      </BdsTableCell>

                      <BdsTableCell type="custom">
                        <BdsChipTag color={chipTagColor(row.status)}>{chipTagText(row.status)}</BdsChipTag>
                      </BdsTableCell>
                      <BdsTableCell type="custom" style={{ maxWidth: '5rem' }}>
                        <>
                          <BdsButtonIcon
                            onClick={() => {
                              handleDeleteCertificateAlert(row.certificate_id);
                            }}
                            size="short"
                            variant="secondary"
                            icon="trash"
                          ></BdsButtonIcon>
                          <BdsButtonIcon
                            onClick={() => {
                              handleEditCertificateAlert(row.certificate_id);
                            }}
                            size="short"
                            variant="secondary"
                            icon="edit"
                          ></BdsButtonIcon>
                        </>
                      </BdsTableCell>
                    </BdsTableRow>
                  );
                })}
              </BdsTableBody>
            </BdsTable>
          )}
          <RemoveCertificate />
          <BdsModal id="hosts-modal" size="dynamic" title={content.hostsModalTitle}>
            <ListHosts />
          </BdsModal>
        </BdsGrid>
      </BdsGrid>
    </BdsPaper>
  );
};

export default ListCertificates;
